import React, { useState, useEffect, KeyboardEvent } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Close, Edit as EditIcon } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { MarkdownContent } from '../MarkdownContent';
import { MatchMessage, MatchInteractionType, MatchInfoField } from './types';

interface MatchInteractionPopupProps {
  /** The message that needs user interaction, or null if none */
  message: MatchMessage | null;
  onSelectOption: (messageId: string, optionId: string, otherText?: string) => void;
  onSelectMultipleOptions: (messageId: string, optionIds: string[], otherText?: string) => void;
  onSubmitText: (messageId: string, text: string) => void;
  onSubmitInfoGather: (messageId: string, responses: Record<string, string>) => void;
  onDismiss?: () => void;
}

export const MatchInteractionPopup: React.FC<MatchInteractionPopupProps> = ({
  message,
  onSelectOption,
  onSelectMultipleOptions,
  onSubmitText,
  onSubmitInfoGather,
  onDismiss,
}) => {
  const theme = useTheme();
  const open = message !== null;
  const interactionType = message ? getInteractionType(message) : null;
  const isMultiSelect = message?.multiSelect ?? false;

  // --- choice state (single select) ---
  const [selectedChoiceId, setSelectedChoiceId] = useState<string | null>(null);
  // --- choice state (multi select) ---
  const [selectedChoiceIds, setSelectedChoiceIds] = useState<Set<string>>(new Set());
  const [otherText, setOtherText] = useState('');

  // --- text state ---
  const [textInput, setTextInput] = useState('');

  // --- info_gather state ---
  const [infoFieldValues, setInfoFieldValues] = useState<Record<string, string>>({});
  const [infoFieldChoices, setInfoFieldChoices] = useState<Record<string, string>>({});

  // Reset state when message changes
  useEffect(() => {
    setSelectedChoiceId(null);
    setSelectedChoiceIds(new Set());
    setOtherText('');
    setTextInput('');
    setInfoFieldValues({});
    setInfoFieldChoices({});
  }, [message?.id]);

  if (!message || !interactionType) return null;

  // Filter out the "Other" option from the visible list (it becomes the bottom text input)
  const visibleOptions = (message.options || []).filter((o) => !o.isOther);
  const hasOtherOption = (message.options || []).some((o) => o.isOther);
  const isChoiceType = interactionType === 'choice_with_other';

  // --- handlers ---
  const handleChoiceClick = (optionId: string) => {
    if (isMultiSelect) {
      setSelectedChoiceIds((prev) => {
        const next = new Set(prev);
        if (next.has(optionId)) {
          next.delete(optionId);
        } else {
          next.add(optionId);
        }
        return next;
      });
      return;
    }
    // Single-select: toggle or select
    setSelectedChoiceId((prev) => (prev === optionId ? null : optionId));
    setOtherText('');
  };

  const handleChoiceSubmit = () => {
    if (!message) return;

    if (isMultiSelect) {
      const ids = Array.from(selectedChoiceIds);
      if (ids.length === 0 && !otherText.trim()) return;
      onSelectMultipleOptions(
        message.id,
        ids,
        otherText.trim() ? otherText.trim() : undefined
      );
      return;
    }

    // Single-select
    if (otherText.trim()) {
      const otherOption = message.options?.find((o) => o.isOther);
      if (otherOption) {
        onSelectOption(message.id, otherOption.id, otherText.trim());
      }
    } else if (selectedChoiceId) {
      onSelectOption(message.id, selectedChoiceId);
    }
  };

  const handleTextSubmit = () => {
    if (!message || !textInput.trim()) return;
    onSubmitText(message.id, textInput.trim());
  };

  const handleTextKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleTextSubmit();
    }
  };

  const handleInfoGatherSubmit = () => {
    if (!message) return;
    const responses: Record<string, string> = {};
    for (const field of message.infoFields || []) {
      if (field.type === 'text') {
        responses[field.id] = infoFieldValues[field.id] || '';
      } else if (field.type === 'choice') {
        responses[field.id] = infoFieldChoices[field.id] || '';
      }
    }
    onSubmitInfoGather(message.id, responses);
  };

  const isChoiceValid = isMultiSelect
    ? selectedChoiceIds.size > 0 || otherText.trim().length > 0
    : selectedChoiceId !== null || otherText.trim().length > 0;
  const isTextValid = textInput.trim().length > 0;
  const isInfoValid = isInfoGatherValid(message.infoFields || [], infoFieldValues, infoFieldChoices);

  const canSubmit =
    isChoiceType ? isChoiceValid :
    interactionType === 'text_only' ? isTextValid :
    interactionType === 'info_gather' ? isInfoValid :
    false;

  const handleSubmit = () => {
    if (isChoiceType) {
      handleChoiceSubmit();
    } else if (interactionType === 'text_only') {
      handleTextSubmit();
    } else if (interactionType === 'info_gather') {
      handleInfoGatherSubmit();
    }
  };

  const titleLabel =
    interactionType === 'text_only' ? 'Response Required' :
    interactionType === 'info_gather' ? 'Additional Information Required' :
    isMultiSelect ? 'Select One or More Options' :
    'Select an Option';

  const submitLabel = canSubmit ? 'Submit' : 'Skip';

  return (
    <Collapse in={open}>
      <Divider />
      <Paper
        elevation={0}
        square
        sx={{
          maxHeight: '50vh',
          overflowY: 'auto',
          bgcolor: alpha(theme.palette.background.default, 0.6),
          backdropFilter: 'blur(8px)',
        }}
      >
        {/* Title bar with close button */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            pt: 1.5,
            pb: 0.5,
          }}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            {titleLabel}
          </Typography>
          {onDismiss && (
            <IconButton size="small" onClick={onDismiss} sx={{ ml: 1, p: 0.5 }}>
              <Close sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>

        {/* Question content */}
        <Box sx={{ px: 2, pb: 1, fontSize: '0.85rem', color: 'text.secondary', lineHeight: 1.6 }}>
          <MarkdownContent content={message.content} />
        </Box>

        {/* Controls */}
        <Box sx={{ px: 2, pb: 1.5 }}>
          {/* === Choice options (choice_with_other) === */}
          {isChoiceType && (
            <Box>
              {visibleOptions.map((option, index) => {
                const isChosen = isMultiSelect
                  ? selectedChoiceIds.has(option.id)
                  : selectedChoiceId === option.id;
                const isFirst = index === 0;

                return (
                  <React.Fragment key={option.id}>
                    <Box
                      onClick={() => handleChoiceClick(option.id)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        py: 1.25,
                        px: 0.5,
                        cursor: 'pointer',
                        borderRadius: 1,
                        transition: 'background-color 0.15s ease',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.action.hover, 0.04),
                        },
                      }}
                    >
                      {isMultiSelect ? (
                        <Checkbox
                          size="small"
                          checked={isChosen}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => handleChoiceClick(option.id)}
                          sx={{
                            p: 0,
                            color: alpha(theme.palette.text.primary, 0.3),
                            '&.Mui-checked': { color: '#cc5544' },
                          }}
                        />
                      ) : (
                        /* Numbered circle */
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            fontSize: '0.8rem',
                            fontWeight: 700,
                            transition: 'all 0.15s ease',
                            ...(isChosen
                              ? {
                                  bgcolor: '#cc5544',
                                  color: 'white',
                                  border: '2px solid #cc5544',
                                }
                              : {
                                  bgcolor: 'transparent',
                                  color: 'text.secondary',
                                  border: `2px solid ${alpha(theme.palette.text.primary, 0.2)}`,
                                }),
                          }}
                        >
                          {index + 1}
                        </Box>
                      )}
                      <Typography
                        variant="body2"
                        fontWeight={isChosen ? 600 : 400}
                        sx={{ flex: 1 }}
                      >
                        {option.label}
                        {isFirst && (
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{ ml: 0.75, color: 'text.secondary', fontWeight: 400, fontStyle: 'italic' }}
                          >
                            (recommended)
                          </Typography>
                        )}
                      </Typography>
                    </Box>
                    {index < visibleOptions.length - 1 && (
                      <Divider sx={{ opacity: 0.4 }} />
                    )}
                  </React.Fragment>
                );
              })}

              {/* "Other" bottom input row for choice_with_other */}
              {hasOtherOption && interactionType === 'choice_with_other' && (
                <>
                  <Divider sx={{ opacity: 0.4 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Something else"
                      value={otherText}
                      onChange={(e) => {
                        setOtherText(e.target.value);
                        if (e.target.value.trim()) {
                          setSelectedChoiceId(null);
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EditIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          fontSize: '0.85rem',
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: alpha(theme.palette.divider, 0.3),
                          },
                        },
                      }}
                    />
                  </Box>
                </>
              )}

              {/* Submit / Skip button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                <Button
                  variant={canSubmit ? 'contained' : 'text'}
                  size="small"
                  onClick={canSubmit ? handleSubmit : onDismiss}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 2,
                    fontWeight: 600,
                    px: 3,
                    ...(canSubmit
                      ? {
                          bgcolor: '#cc5544',
                          color: 'white',
                          '&:hover': { bgcolor: '#b54636' },
                        }
                      : {
                          color: 'text.secondary',
                        }),
                  }}
                >
                  {submitLabel}
                </Button>
              </Box>
            </Box>
          )}

          {/* === Text only === */}
          {interactionType === 'text_only' && (
            <>
              <TextField
                fullWidth
                size="small"
                placeholder={message.textPrompt || 'Type your response...'}
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                onKeyDown={handleTextKeyDown}
                multiline
                minRows={2}
                maxRows={4}
                autoFocus
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.85rem',
                    borderRadius: 2,
                  },
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                <Button
                  variant={canSubmit ? 'contained' : 'text'}
                  size="small"
                  onClick={canSubmit ? handleSubmit : onDismiss}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 2,
                    fontWeight: 600,
                    px: 3,
                    ...(canSubmit
                      ? {
                          bgcolor: '#cc5544',
                          color: 'white',
                          '&:hover': { bgcolor: '#b54636' },
                        }
                      : {
                          color: 'text.secondary',
                        }),
                  }}
                >
                  {canSubmit ? 'Submit' : 'Skip'}
                </Button>
              </Box>
            </>
          )}

          {/* === Info gather === */}
          {interactionType === 'info_gather' && (
            <>
              <Stack spacing={2}>
                {(message.infoFields || []).map((field) => (
                  <InfoGatherField
                    key={field.id}
                    field={field}
                    choiceValue={infoFieldChoices[field.id] || ''}
                    textValue={infoFieldValues[field.id] || ''}
                    onChoiceSelect={(optionId) =>
                      setInfoFieldChoices((prev) => ({ ...prev, [field.id]: optionId }))
                    }
                    onTextChange={(value) =>
                      setInfoFieldValues((prev) => ({ ...prev, [field.id]: value }))
                    }
                    theme={theme}
                  />
                ))}
              </Stack>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                <Button
                  variant={canSubmit ? 'contained' : 'text'}
                  size="small"
                  onClick={canSubmit ? handleSubmit : onDismiss}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 2,
                    fontWeight: 600,
                    px: 3,
                    ...(canSubmit
                      ? {
                          bgcolor: '#cc5544',
                          color: 'white',
                          '&:hover': { bgcolor: '#b54636' },
                        }
                      : {
                          color: 'text.secondary',
                        }),
                  }}
                >
                  {canSubmit ? 'Submit' : 'Skip'}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Paper>
    </Collapse>
  );
};

/** Determine the effective interaction type from a message */
function getInteractionType(message: MatchMessage): MatchInteractionType | null {
  if (message.role === 'user') return null;
  if (message.interactionType) return message.interactionType;
  if (message.infoFields && message.infoFields.length > 0) return 'info_gather';
  if (message.textPrompt) return 'text_only';
  if (message.options && message.options.length > 0) {
    return 'choice_with_other';
  }
  return null;
}

/** Check if all required info-gather fields are filled */
function isInfoGatherValid(
  fields: MatchInfoField[],
  textValues: Record<string, string>,
  choiceValues: Record<string, string>
): boolean {
  for (const field of fields) {
    if (!field.required) continue;
    if (field.type === 'text' && !textValues[field.id]?.trim()) return false;
    if (field.type === 'choice' && !choiceValues[field.id]) return false;
  }
  return true;
}

/** Renders a single field inside the info-gather form */
const InfoGatherField: React.FC<{
  field: MatchInfoField;
  choiceValue: string;
  textValue: string;
  onChoiceSelect: (optionId: string) => void;
  onTextChange: (value: string) => void;
  theme: Theme;
}> = ({ field, choiceValue, textValue, onChoiceSelect, onTextChange, theme }) => {
  return (
    <Box>
      <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75 }}>
        {field.label}
        {field.required && (
          <Typography component="span" color="error" sx={{ ml: 0.5 }}>
            *
          </Typography>
        )}
      </Typography>

      {field.type === 'choice' && field.options && (
        <Box>
          {field.options.map((option, index) => {
            const isChosen = choiceValue === option.id;
            return (
              <React.Fragment key={option.id}>
                <Box
                  onClick={() => onChoiceSelect(option.id)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1,
                    px: 0.5,
                    cursor: 'pointer',
                    borderRadius: 1,
                    transition: 'background-color 0.15s ease',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.action.hover, 0.04),
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      transition: 'all 0.15s ease',
                      ...(isChosen
                        ? {
                            bgcolor: '#cc5544',
                            color: 'white',
                            border: '2px solid #cc5544',
                          }
                        : {
                            bgcolor: 'transparent',
                            color: 'text.secondary',
                            border: `2px solid ${alpha(theme.palette.text.primary, 0.2)}`,
                          }),
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight={isChosen ? 600 : 400}
                  >
                    {option.label}
                  </Typography>
                </Box>
                {index < field.options!.length - 1 && (
                  <Divider sx={{ opacity: 0.4 }} />
                )}
              </React.Fragment>
            );
          })}
        </Box>
      )}

      {field.type === 'text' && (
        <TextField
          fullWidth
          size="small"
          placeholder={field.placeholder || 'Enter details...'}
          value={textValue}
          onChange={(e) => onTextChange(e.target.value)}
          multiline={field.multiline}
          minRows={field.multiline ? 2 : 1}
          maxRows={field.multiline ? 4 : 1}
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: '0.85rem',
              borderRadius: 2,
            },
          }}
        />
      )}
    </Box>
  );
};
